import Responsive from "components/Responsive";
import { IntersectionObserverOptions } from "hooks/useIntersectionObserver";
import useLanguage from "hooks/useLanguage";
import useOnScreen from "hooks/useOnScreen";
import useResizeObserver from "hooks/useResizeObserver";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { clamp } from "utils/math";
import {
  AnimationImages,
  getFrameIndex,
  getScrollTop,
  preloadImages,
} from "./helper";
import { BottomGradient, Container, ImageContainer } from "./styles";

const imageContainerId = "image-container";
const images: AnimationImages = {
  pt: [],
  en: [],
  es: [],
};

const preloadIntersectionOptions: IntersectionObserverOptions = {
  rootMargin: "400px",
  unobserveOnIntersection: true,
};

const intersectionOptions: IntersectionObserverOptions = {
  threshold: 1,
};

const ParkingMeterAnimation: React.FC = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(imageContainerRef, intersectionOptions);
  const shouldPreload = useOnScreen(
    imageContainerRef,
    preloadIntersectionOptions
  );

  useEffect(() => {
    if (shouldPreload) {
      preloadImages(images, t, language);
    }
  }, [language, shouldPreload, t]);

  const updateFrame = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const scrollTop = getScrollTop();
    const containerBoundingRect = containerRef.current.getBoundingClientRect();
    const containerYTop = containerBoundingRect.y + scrollTop;
    const containerHeight = containerBoundingRect.height;
    const maxScroll = containerHeight - window.innerHeight;
    const currentScroll = scrollTop - containerYTop;
    const scrollFraction = clamp(currentScroll / maxScroll, [0, 1]);
    const frameIndex = getFrameIndex(scrollFraction);

    requestAnimationFrame(() => {
      document
        .getElementById(imageContainerId)
        ?.firstChild?.replaceWith(images[language][frameIndex]);
    });
  }, [language]);

  useEffect(() => {
    if (!isOnScreen) {
      return undefined;
    }

    window.addEventListener("scroll", updateFrame);

    return () => window.removeEventListener("scroll", updateFrame);
  }, [isOnScreen, updateFrame]);

  useResizeObserver(
    containerRef,
    useCallback(() => {
      if (shouldPreload) {
        updateFrame();
      }
    }, [shouldPreload, updateFrame])
  );

  return (
    <Container ref={containerRef}>
      <ImageContainer ref={imageContainerRef} id={imageContainerId}>
        <div />
      </ImageContainer>
      <Responsive minWidth="2000px">
        <BottomGradient />
      </Responsive>
    </Container>
  );
};

export default ParkingMeterAnimation;
